import { useEffect, useState } from 'react';
import { api, type BidDetail, type ImportResult, type RebuildResult, type SupplierOption } from '../api';
import { money, ts, unit, useDebounced, useLoad } from '../lib';
import { CategoriesCard, ChangeLog, ImportOutcome, MagentoNote, Outcome, pack, Pager, SelectAll, StatusPill, SupplierPicker, useSelection } from './catalog-ui';

// Bids (ADOBE_ADMIN_PARITY 2026-10-16, Tier 1 #5) — production's Manage Bids, the bid spreadsheet
// export and import, and Manage Associations. A bid names the masters it covers (by SKU prefix and
// category, or by hand), the suppliers invited to price them, and the spreadsheet each supplier fills
// in. Commerce writes and audits every change (routes/bids.ts).

export function BidsPanel({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [nonce, setNonce] = useState(0);
  const { data, err } = useLoad(() => api.bidsList(dq), [dq, nonce]);
  const [open, setOpen] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [bad, setBad] = useState('');

  if (open != null) return <BidEditor id={open} canEdit={canEdit} onBack={() => { setOpen(null); setNonce((n) => n + 1); }} />;

  async function create() {
    setBad('');
    try { const r = await api.bidCreate({ name }); setName(''); setOpen(r.id); } catch (e) { setBad((e as Error).message); }
  }

  return (
    <div className="stack">
      <h1>Bids</h1>
      <p className="sub">Each bid: the masters it covers, the suppliers invited to price them, and the spreadsheet they fill in</p>
      <div className="toolbar">
        <input className="search" placeholder="Search bids…" value={q} onChange={(e) => setQ(e.target.value)} />
        {canEdit && (
          <span className="toolbar-end inline-field">
            <input className="search" placeholder="New bid name" value={name} onChange={(e) => setName(e.target.value)} />
            <button className="btn-sm" disabled={!name.trim()} onClick={create}>Add bid</button>
          </span>
        )}
      </div>
      <Outcome msg="" err={bad || err || ''} />
      <section className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Id</th><th>Bid</th><th>SKU prefixes</th><th className="num">Categories</th><th className="num">Masters</th><th className="num">Suppliers</th><th>Updated</th></tr></thead>
            <tbody>
              {data?.map((b) => (
                <tr key={b.id} className="row-click" onClick={() => setOpen(b.id)}>
                  <td className="mono">{b.id}</td>
                  <td style={{ fontWeight: 600 }}>{b.name}</td>
                  <td className="mono">{b.sku_prefix || '—'}</td>
                  <td className="num">{b.categories}</td>
                  <td className="num">{b.masters.toLocaleString()}</td>
                  <td className="num">{b.suppliers}</td>
                  <td className="mono">{ts(b.updated_at)}</td>
                </tr>
              ))}
              {data && data.length === 0 && <tr><td colSpan={7} className="loading">No bids match.</td></tr>}
            </tbody>
          </table>
        </div>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}

function BidEditor({ id, canEdit, onBack }: { id: number; canEdit: boolean; onBack: () => void }) {
  const [d, setD] = useState<BidDetail | null>(null);
  const [nonce, setNonce] = useState(0);
  const [form, setForm] = useState({ name: '', sku_prefix: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const load = () => api.bid(id).then((r) => { setD(r); setForm({ name: r.bid.name, sku_prefix: r.bid.sku_prefix ?? '' }); }).catch((e) => setErr((e as Error).message));
  useEffect(() => { load(); }, [id, nonce]);
  const refresh = () => setNonce((n) => n + 1);
  const run = async (fn: () => Promise<string>) => {
    setMsg(''); setErr('');
    try { setMsg(await fn()); refresh(); } catch (e) { setErr((e as Error).message); }
  };

  if (!d) return <div className="stack"><div><button className="btn-sm ghost" onClick={onBack}>← Back to bids</button></div>{err ? <p className="msg bad">⚠ {err}</p> : <p className="loading">Loading…</p>}</div>;
  const b = d.bid;
  const dirty = form.name !== b.name || form.sku_prefix !== (b.sku_prefix ?? '');

  return (
    <div className="stack">
      <div><button className="btn-sm ghost" onClick={onBack}>← Back to bids</button></div>
      <h1>{b.name}</h1>
      <p className="sub">Bid {b.id} · {b.masters.toLocaleString()} masters · {d.suppliers.length} suppliers{b.updated_at ? ` · updated ${ts(b.updated_at)}` : ''}</p>
      <Outcome msg={msg} err={err} />

      <section className="card">
        <h2>Bid</h2>
        <fieldset className="plain" disabled={!canEdit}>
          <div className="tf-grid">
            <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>SKU prefixes (comma-separated)<input value={form.sku_prefix} onChange={(e) => setForm({ ...form, sku_prefix: e.target.value })} placeholder="hfbb, hfkf" /></label>
          </div>
        </fieldset>
        {canEdit && (
          <div className="edi-actions">
            <span className="muted">Rebuild Products uses the prefixes and the categories below.</span>
            <span className="actions">
              <button className="btn-sm" disabled={!dirty} onClick={() => run(async () => { const r = await api.bidSave(id, form); return r.changed.length ? 'Bid saved.' : 'Nothing changed.'; })}>Save</button>
              <button className="btn-sm ghost danger" onClick={() => {
                if (!window.confirm(`Delete ${b.name}? Its id is taken off every association; the change log keeps its last values.`)) return;
                api.bidDelete(id).then(onBack).catch((e) => setErr((e as Error).message));
              }}>Delete bid</button>
            </span>
          </div>
        )}
      </section>

      <SuppliersCard d={d} canEdit={canEdit} onSave={(ids) => run(async () => { await api.bidSave(id, { supplier_ids: ids }); return 'Suppliers saved.'; })} />
      <CategoriesCard ids={d.bid.category_ids} shown={d.categories} canEdit={canEdit}
        onSave={(ids) => run(async () => { await api.bidSave(id, { category_ids: ids }); return 'Categories saved.'; })} />
      <MastersCard id={id} canEdit={canEdit} nonce={nonce} onChange={(text) => { setMsg(text); refresh(); }} onError={setErr} />
      <SheetCard d={d} canEdit={canEdit} onImported={refresh} />
      {d.imports.length > 0 && (
        <section className="card">
          <h2>Imports and exports</h2>
          <table>
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>Supplier</th><th>File</th><th>Result</th></tr></thead>
            <tbody>
              {d.imports.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{ts(r.created_at)}</td><td>{r.created_by}</td>
                  <td>{r.kind === 'bid_export' ? 'Export' : `Import${r.mode ? ` (${r.mode.replace(/_/g, ' ')})` : ''}`}{r.dry_run ? <span className="pill info"> preview</span> : null}</td>
                  <td>{r.vendor ?? '—'}</td><td className="mono">{r.filename ?? '—'}</td><td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <ChangeLog changes={d.changes} />
      {canEdit && <MagentoNote />}
    </div>
  );
}

function SuppliersCard({ d, canEdit, onSave }: { d: BidDetail; canEdit: boolean; onSave: (ids: number[]) => void }) {
  const [list, setList] = useState(d.suppliers.map((s) => ({ id: s.id, name: s.name, products: s.products })));
  useEffect(() => { setList(d.suppliers.map((s) => ({ id: s.id, name: s.name, products: s.products }))); }, [d]);
  const dirty = list.map((s) => s.id).join() !== d.suppliers.map((s) => s.id).join();
  return (
    <section className="card">
      <h2>Assigned suppliers</h2>
      <p className="muted" style={{ marginTop: 0 }}>The suppliers invited to price this bid. A bid sheet is imported only for an assigned supplier.</p>
      <div className="picked-row">
        {list.map((s) => (
          <span key={s.id} className="pill cat-tag">
            {s.name ?? `Supplier ${s.id}`} <span className="muted">· {s.products} on bid</span>
            {canEdit && <button className="link" aria-label={`Remove ${s.name}`} onClick={() => setList(list.filter((x) => x.id !== s.id))}> ✕</button>}
          </span>
        ))}
        {list.length === 0 && <span className="muted">No supplier is assigned.</span>}
      </div>
      {canEdit && (
        <div className="form-row">
          <SupplierPicker value={null} label="Add a supplier" onChange={(s: SupplierOption | null) => {
            if (s && !list.some((x) => x.id === s.id)) setList([...list, { id: s.id, name: s.name, products: 0 }]);
          }} />
          <button className="btn-sm" disabled={!dirty} onClick={() => onSave(list.map((s) => s.id))}>Save suppliers</button>
        </div>
      )}
    </section>
  );
}

function MastersCard({ id, canEdit, nonce, onChange, onError }: { id: number; canEdit: boolean; nonce: number; onChange: (msg: string) => void; onError: (e: string) => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 300);
  const [page, setPage] = useState(1);
  const { data } = useLoad(() => api.bidMasters(id, dq, page), [id, dq, page, nonce]);
  const sel = useSelection();
  const [add, setAdd] = useState('');
  const [preview, setPreview] = useState<RebuildResult | null>(null);
  const call = async (fn: () => Promise<string>) => { try { onChange(await fn()); } catch (e) { onError((e as Error).message); } };
  const ids = data?.rows.map((r) => r.id) ?? [];
  return (
    <section className="card">
      <h2>Masters {data && <span className="muted">({data.total.toLocaleString()})</span>}</h2>
      {canEdit && (
        <div className="bulk-bar">
          <button className="btn-sm ghost" onClick={() => call(async () => { const r = await api.bidRebuild(id, true); setPreview(r); return ''; })}>Preview Rebuild Products</button>
          {preview && (
            <span className="inline-field">
              Rebuild would keep {preview.after.toLocaleString()} masters: +{preview.added.toLocaleString()} / −{preview.removed.toLocaleString()} ({preview.associations.toLocaleString()} associations).
              <button className="btn-sm" onClick={() => call(async () => {
                const r = await api.bidRebuild(id, false); setPreview(null);
                return `Rebuilt: ${r.after.toLocaleString()} masters (+${r.added} / −${r.removed}).`;
              })}>Rebuild</button>
            </span>
          )}
        </div>
      )}
      <div className="toolbar">
        <input className="search" placeholder="Search this bid's masters…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        {canEdit && (
          <span className="toolbar-end inline-field">
            <input className="search" placeholder="Add master SKUs (space or comma)" value={add} onChange={(e) => setAdd(e.target.value)} />
            <button className="btn-sm" disabled={!add.trim()} onClick={() => call(async () => {
              const r = await api.bidEditMasters(id, { add }); setAdd('');
              return `Added ${r.added} master${r.added === 1 ? '' : 's'} (${r.associations} associations now carry the bid).`;
            })}>Add</button>
            <button className="btn-sm ghost" disabled={sel.sel.size === 0} onClick={() => call(async () => {
              const r = await api.bidEditMasters(id, { remove: [...sel.sel] }); sel.clear();
              return `Removed ${r.removed} master${r.removed === 1 ? '' : 's'} from the bid.`;
            })}>Remove selected</button>
          </span>
        )}
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr>{canEdit && <th className="chk-col"><SelectAll ids={ids} selection={sel} /></th>}<th>Master SKU</th><th>Name</th><th>Type</th><th className="num">Supplier products</th><th>Status</th></tr></thead>
          <tbody>
            {data?.rows.map((m) => (
              <tr key={m.id}>
                {canEdit && <td className="chk-col"><input type="checkbox" aria-label={`Select ${m.sku}`} checked={sel.has(m.id)} onChange={() => sel.toggle(m.id)} /></td>}
                <td className="mono">{m.sku}</td><td>{m.name ?? '—'}</td><td>{m.attribute_set_type ?? '—'}</td><td className="num">{m.associations}</td><td><StatusPill status={m.status} /></td>
              </tr>
            ))}
            {data && data.rows.length === 0 && <tr><td colSpan={6} className="loading">No masters.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && <Pager page={data.page} per={data.per} total={data.total} onPage={setPage} />}
      {data && data.missing > 0 && <p className="muted small">{data.missing.toLocaleString()} ids on this bid name no master in the catalog.</p>}
    </section>
  );
}

const OPTIONAL = [
  ['master_kosher', 'Kosher / Non-Kosher'], ['is_catch_weight', 'Catch weight'], ['special_order', 'Special order'], ['direct_ship', 'Direct ship'],
  ['early_cutoff', 'Early cutoff'], ['gluten_free', 'Gluten free'], ['kosher_type', 'Kosher type'], ['vegan', 'Vegan'], ['master_created_at', 'Tavezio created'],
] as const;

/** The bid spreadsheet: export one supplier's sheet, and import what it sends back. */
function SheetCard({ d, canEdit, onImported }: { d: BidDetail; canEdit: boolean; onImported: () => void }) {
  const [vendorId, setVendorId] = useState<number | ''>(d.suppliers[0]?.id ?? '');
  const [opt, setOpt] = useState<Set<string>>(new Set(['master_kosher', 'gluten_free', 'kosher_type', 'vegan']));
  const [good, setGood] = useState({ from: '', to: '' });
  const [mode, setMode] = useState('traditional');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState('');
  const [r, setR] = useState<ImportResult | null>(null);
  const [err, setErr] = useState('');
  const href = vendorId === '' ? undefined
    : `/api/commerce/bids/${d.bid.id}/export.xlsx?${new URLSearchParams({ vendorId: String(vendorId), optional: [...opt].join(','), goodFrom: good.from, goodTo: good.to })}`;
  async function go(dryRun: boolean) {
    if (vendorId === '' || !file) return;
    setBusy(dryRun ? 'preview' : 'import'); setErr(''); setR(null);
    try { setR(await api.bidImport(d.bid.id, vendorId, mode, file, dryRun)); onImported(); } catch (e) { setErr((e as Error).message); } finally { setBusy(''); }
  }
  return (
    <section className="card">
      <h2>Bid spreadsheet</h2>
      {d.suppliers.length === 0 ? <p className="muted">Assign a supplier first: the sheet is per supplier.</p> : (
        <>
          <div className="form-row">
            <label className="field"><span>Supplier</span>
              <select className="sel" value={vendorId} onChange={(e) => setVendorId(Number(e.target.value))}>
                {d.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name ?? s.id}</option>)}
              </select>
            </label>
            <label className="field"><span>Good from</span><input type="date" className="sel" value={good.from} onChange={(e) => setGood({ ...good, from: e.target.value })} /></label>
            <label className="field"><span>Good to</span><input type="date" className="sel" value={good.to} onChange={(e) => setGood({ ...good, to: e.target.value })} /></label>
            <a className={`btn-sm${href ? '' : ' disabled'}`} href={href} download>Export sheet</a>
          </div>
          <div className="feed-cfg" style={{ marginTop: 8 }}>
            Optional columns:
            {OPTIONAL.map(([k, l]) => (
              <label key={k} className="chk"><input type="checkbox" checked={opt.has(k)} onChange={(e) => {
                const n = new Set(opt); if (e.target.checked) n.add(k); else n.delete(k); setOpt(n);
              }} />{l}</label>
            ))}
          </div>
          {canEdit && (
            <>
              <h2 style={{ marginTop: 16 }}>Import the supplier's sheet</h2>
              <p className="muted" style={{ marginTop: 0 }}>
                New item numbers become supplier products; a changed price waits for approval under EDI → price requests (a 0 price is applied at once).
                <strong> Traditional</strong> also removes the supplier's products on each listed master that the sheet no longer lists; <strong>add items only</strong> never removes.
              </p>
              <div className="form-row">
                <label className="field"><span>Mode</span>
                  <select className="sel" value={mode} onChange={(e) => setMode(e.target.value)}>
                    <option value="traditional">Traditional</option><option value="add_items_only">Add items only</option>
                  </select>
                </label>
                <label className="field"><span>File (.xlsx)</span><input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
                <button className="btn-sm ghost" disabled={!file || busy !== ''} onClick={() => go(true)}>{busy === 'preview' ? 'Previewing…' : 'Preview'}</button>
                <button className="btn-sm" disabled={!file || busy !== ''} onClick={() => go(false)}>{busy === 'import' ? 'Importing…' : 'Import'}</button>
              </div>
              <Outcome msg="" err={err} />
              {r && <ImportOutcome r={r} />}
            </>
          )}
        </>
      )}
    </section>
  );
}

// ── Manage Associations ───────────────────────────────────────────────────────────────────────────
export function AssociationsPanel({ canEdit }: { canEdit: boolean }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 300);
  const [supplier, setSupplier] = useState<SupplierOption | null>(null);
  const [f, setF] = useState({ bidId: '', assigned: '', pending: '' });
  const [page, setPage] = useState(1);
  const [nonce, setNonce] = useState(0);
  const bids = useLoad(() => api.bidsList(), []);
  const params = { q: dq, vendorId: supplier ? String(supplier.id) : '', ...f };
  const { data, err } = useLoad(() => api.associations(Object.fromEntries(Object.entries({ ...params, page: String(page) }).filter(([, v]) => v !== ''))),
    [JSON.stringify(params), page, nonce]);
  useEffect(() => { setPage(1); }, [JSON.stringify(params)]);
  const sel = useSelection();
  const [msg, setMsg] = useState('');
  const [bad, setBad] = useState('');
  const [link, setLink] = useState({ offer: '', master: '' });
  const run = async (fn: () => Promise<string>) => {
    setMsg(''); setBad('');
    try { setMsg(await fn()); setNonce((n) => n + 1); } catch (e) { setBad((e as Error).message); }
  };
  const bulk = (action: string, what: string) => run(async () => {
    const r = await api.associationsBulk([...sel.sel], action); sel.clear();
    return `${what}: ${r.changed.toLocaleString()} of ${r.selected.toLocaleString()} associations.`;
  });
  const ids = data?.rows.map((r) => r.id) ?? [];

  return (
    <div className="stack">
      <h1>Associations</h1>
      <p className="sub">Which supplier products fill which masters — with the price, a pending price request, and the 30/60-day average price</p>
      <div className="toolbar toolbar-fields">
        <label className="field"><span>Search</span><input className="search" placeholder="Master SKU or name, vendor item #…" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        <SupplierPicker value={supplier} onChange={setSupplier} />
        <label className="field"><span>Bid</span>
          <select className="sel" value={f.bidId} onChange={(e) => setF({ ...f, bidId: e.target.value })}>
            <option value="">Any bid</option>{bids.data?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label className="field"><span>Assigned</span>
          <select className="sel" value={f.assigned} onChange={(e) => setF({ ...f, assigned: e.target.value })}>
            <option value="">Any</option><option value="1">Yes</option><option value="0">No</option>
          </select>
        </label>
        <button className={`chip${f.pending === '1' ? ' on' : ''}`} onClick={() => setF({ ...f, pending: f.pending === '1' ? '' : '1' })}>Pending price requests</button>
      </div>
      <Outcome msg={msg} err={bad || err || ''} />
      {canEdit && sel.sel.size > 0 && (
        <div className="bulk-bar">
          <strong>{sel.sel.size.toLocaleString()} selected</strong>
          <button className="btn-sm" onClick={() => bulk('assign', 'Assigned')}>Assign</button>
          <button className="btn-sm" onClick={() => bulk('unassign', 'Unassigned')}>Unassign</button>
          <button className="btn-sm ghost" onClick={() => bulk('clear_request', 'Price requests cleared')}>Clear price request</button>
          <button className="btn-sm ghost danger" onClick={() => {
            if (window.confirm(`Delete ${sel.sel.size} association(s)? The supplier products stop filling those masters.`)) bulk('delete', 'Deleted');
          }}>Delete association</button>
          <button className="link" onClick={sel.clear}>Clear selection</button>
        </div>
      )}
      <section className="card">
        <div className="table-wrap">
          <table>
            <thead><tr>
              {canEdit && <th className="chk-col"><SelectAll ids={ids} selection={sel} /></th>}
              <th>Supplier</th><th>Vendor item #</th><th>Master</th><th>Pack</th><th className="num">Price</th><th className="num">Unit</th>
              <th className="num">Requested</th><th className="num">Change</th><th className="num">30-day avg</th><th className="num">60-day avg</th><th>Auto-approve</th><th>Assigned</th><th>Bids</th>
            </tr></thead>
            <tbody>
              {data?.rows.map((a) => (
                <tr key={a.id}>
                  {canEdit && <td className="chk-col"><input type="checkbox" aria-label={`Select association ${a.id}`} checked={sel.has(a.id)} onChange={() => sel.toggle(a.id)} /></td>}
                  <td>{a.vendor ?? '—'}</td>
                  <td className="mono">{a.supplier_sku ?? a.sku}{a.offer_status !== 1 && <span className="muted small"> · disabled</span>}</td>
                  <td><span className="mono">{a.master_sku ?? a.item_id}</span><div className="muted small">{a.master_name ?? ''}</div></td>
                  <td>{pack(a.pack_count, a.pack_size, a.uom)}</td>
                  <td className="num">{money(a.price)}</td>
                  <td className="num">{unit(a.unit_price)}</td>
                  <td className="num">{Number(a.price_change_request) > 0 ? <>{money(a.price_change_request)}<div className="muted small">{unit(a.requested_unit_price)}</div></> : '—'}</td>
                  <td className={`num${a.change_pct != null ? (a.change_pct > 0 ? ' up-bad' : ' down-good') : ''}`}>{a.change_pct == null ? '—' : `${a.change_pct > 0 ? '+' : ''}${a.change_pct}%`}</td>
                  <td className="num">{money(a.avg30)}</td>
                  <td className="num">{money(a.avg60)}</td>
                  <td>{a.auto_approve ?? '—'}</td>
                  <td>{a.is_assign === 1 ? 'Yes' : 'No'}</td>
                  <td className="mono">{a.bids ?? '—'}</td>
                </tr>
              ))}
              {data && data.rows.length === 0 && <tr><td colSpan={14} className="loading">No associations match.</td></tr>}
            </tbody>
          </table>
        </div>
        {!data && !err && <p className="loading">Loading…</p>}
        {data && <Pager page={data.page} per={data.per} total={data.total} onPage={setPage} />}
        <p className="muted small">A pending request is approved under EDI → price requests, which writes the price, its unit price and its history together.</p>
      </section>
      {canEdit && (
        <section className="card">
          <h2>Associate a supplier product with a master</h2>
          <div className="form-row">
            <label className="field"><span>Supplier product id</span><input className="search short" inputMode="numeric" value={link.offer} onChange={(e) => setLink({ ...link, offer: e.target.value })} /></label>
            <label className="field"><span>Master SKU</span><input className="search" value={link.master} onChange={(e) => setLink({ ...link, master: e.target.value })} /></label>
            <button className="btn-sm" disabled={!link.offer || !link.master} onClick={() => run(async () => {
              const r = await api.associationAdd(Number(link.offer), link.master); setLink({ offer: '', master: '' });
              return r.created ? 'Associated.' : 'Already associated.';
            })}>Associate</button>
          </div>
        </section>
      )}
    </div>
  );
}
